import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import './BlogPost.css';
import LoadingSpinner from '../../backend/components/LoadingSpinner';
// Toast imports
import { ToastContainer } from 'react-toastify'; // Import ToastContainer
import 'react-toastify/dist/ReactToastify.css';
import { displayNotification } from './Blog';
import BlogSubscribeForm from '../SubscribeFormFolder/BlogSubscribeForm'
import { useAuth } from '../../Auth';

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component'

// Assets
import temp_image from './img/blog-banner.png';

// Icons
import { LuClock4 } from "react-icons/lu";
import { BiSolidQuoteLeft, BiSolidQuoteRight } from "react-icons/bi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLinkedin,
    faXTwitter,
    faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { CiShare2 } from "react-icons/ci"
import { FaExclamationCircle } from "react-icons/fa";

interface ContentBlock {
    contentID: number;
    block_type: 'paragraph' | 'quote' | 'image' | 'video';
    text: string | null;
    image: string | null;
    image_title: string | null;
    image_description: string | null;
    video_url?: string | null; // <-- Add this
    order: number;
}


interface Blog {
    postID: number;
    link_name: string;
    title: string;
    author: string;
    about_author: string;
    summary: string;
    tags: string;
    date: string;
    specialty: string;
    mainImage: string;
    draft: boolean;
    content_blocks: ContentBlock[];
}

const calculateReadTime = (blog: Blog): number => {
    const wordsPerMinute = 200;
    const paragraphText = blog.content_blocks
        .filter(block => block.block_type === 'paragraph' && block.text)
        .map(block => block.text || '')
        .join(' ');
    const wordCount = paragraphText.split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const handleShareClick = (
    e: React.MouseEvent,
    link_name?: string,
    postID?: number
) => {
    e.stopPropagation();
    const url = link_name
        ? `${window.location.origin}/fountainn/blog/${link_name}`
        : `${window.location.origin}/fountainn/post/${postID}`;

    navigator.clipboard.writeText(url)
        .then(() => displayNotification())
        .catch(err => console.error("Failed to copy: ", err));
};


const BlogPost = () => {
    const { postID, link_name } = useParams<{ postID?: string; link_name?: string }>();
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, getPermissions } = useAuth();
    const [permissionGroups, setPermissionGroups] = useState<{ [key: string]: any }>({});

    // Fetch user permissions
    useEffect(() => {
        const fetchPermissions = async () => {
            const permissions = await getPermissions();
            setPermissionGroups(permissions || {});
        };

        if (isAuthenticated) {
            fetchPermissions();
        }
    }, [isAuthenticated, getPermissions]);

    const isMarketing = (permissionGroups?.Marketing || permissionGroups?.MarketingTeam) === true;


    function getVideoId(url: any) {
        const match = url.match(/vimeo\.com\/(\d+)(?:\/(\w+))?/);
        if (match) {
            const id = match[1];
            const hash = match[2] ? `?h=${match[2]}` : '';
            return `${id}${hash}`;
        }
        return '';
    }

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch('/api/blogposts/', {
                    method: 'GET',
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "same-origin",
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data: Blog[] = await response.json();
                setBlogs(data);
                setLoading(false);
            } catch (err) {
                console.error('Fetch error:', err);
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);


    const selectedBlog = blogs.find(blog => {
        if (link_name) {
            return blog.link_name === link_name
        }
        if (postID) {
            return blog.postID === parseInt(postID, 10);
        }
        return false;
    });

    // Legacy map: old numeric blog links -> current postID
    const legacyMap: { [key: string]: number } = {
        "13": 15, // old /blog/13 -> postID 15
        "14": 16, // old /blog/14 -> postID 16
        "15": 21, // old /blog/15 -> postID 21

        // Additional blogs just to be on the safe side
        "12": 14,
        "11": 13,
        "10": 12,
        "9": 11,
        "8": 10,
        "7": 9,
        "6": 7,
        "5": 6,
        "4": 5,
        "3": 4,
        "2": 2,
        "1": 1,
    };

    // --- Insert this useEffect right here, after selectedBlog is defined ---
    useEffect(() => {
        if (!blogs.length) return; // wait until blogs are loaded

        if (link_name) {
            // Handle old numeric legacy links
            const mappedPostID = legacyMap[link_name];
            if (mappedPostID) {
                const legacyBlog = blogs.find(blog => blog.postID === mappedPostID);
                if (legacyBlog?.link_name) {
                    navigate(`/fountainn/blog/${legacyBlog.link_name}`, { replace: true });
                }
            }
        } else if (postID && selectedBlog?.link_name) {
            // Handle old /post/:postID links
            navigate(`/fountainn/blog/${selectedBlog.link_name}`, { replace: true });
        }
    }, [link_name, postID, selectedBlog, blogs, navigate]);


    if (loading) return <div>
        <div className="blog-hero-section">
            <LazyLoadComponent placeholder={<img src={temp_image} alt="" />}>
                <LazyLoadImage src={temp_image} alt="Banner" className="hero-image3" />
            </LazyLoadComponent>
            <div className="dark-overlay"></div>
            <div className="news-info-wrapper">
                <h1>FountaiNN</h1>
            </div>
        </div>

        <div
            style={{
                height: "75vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        ><LoadingSpinner />
        </div>

    </div>;

    if (
        !selectedBlog ||
        (!isMarketing && (selectedBlog.draft || (selectedBlog.date && new Date(selectedBlog.date) > new Date())))
    ) {
        return (
            <div className="blog-post-hero-section not-blog">
                <img className="contact-hero-image" alt="hero background" src={temp_image} />
                <div className="dark-overlay"></div>
                <div className="overall-blog-post-wrapper">
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FaExclamationCircle /> Blog Not Found
                    </h1>
                </div>
            </div>
        );
    }

    {/* Inline Recommended Card Component */ }
    const RecommendedCard = ({ blog }: { blog: Blog }) => (
        <div className='blog-post-suggestion-card' onClick={() => window.location.href = `/fountainn/post/${blog.postID}`}>
            <div className='blog-card-img-with-tags'>
                <img src={blog.mainImage} alt="Blog" className="blog-card-img" />
                <div className="dark-overlay"></div>
                <div className='blog-tags'>
                    {blog.tags.split(',').map((tag, i) => (
                        <div className='blog-tag' key={i}>{tag.trim()}</div>
                    ))}
                </div>
                <div
                    className="blog-share"
                    onClick={(e) => handleShareClick(e, blog.link_name, blog.postID)}
                >
                    <CiShare2 className='share-icon' />
                </div>
            </div>

            <div className='blog-card-title-and-content'>
                <h1>{blog.title}</h1>
                <p style={{ color: '#6C757D' }} className='p-forsummary' >{blog.summary}</p>
                <div className='blog-credentials blog-credentials-extension'>
                    <p className="blog-date">
                        {blog.date
                            ? new Date(blog.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })
                            : ""}
                    </p>


                    <p>━</p>
                    <LuClock4 style={{ color: '#6C757D' }} />
                    <p className="blog-date">{calculateReadTime(blog)} minute read</p>
                </div>
            </div>
        </div>
    );

    // Suggested Blog Filtering
    const selectedTags = selectedBlog.tags.split(',').map(tag => tag.trim().toLowerCase());

    const now = new Date();

    // Filter blogs that are not draft and not future-dated
    const safeBlogsForRecommendations = blogs.filter(blog => {
        if (!isMarketing) {
            if (blog.draft) return false;
            if (blog.date && new Date(blog.date) > now) return false;
        }
        return true;
    });


    // Recommended blogs: match tags, exclude the selected blog, and only take the 3 most recent
    const recommendedBlogs = safeBlogsForRecommendations
        .filter(blog =>
            blog.postID !== selectedBlog.postID &&
            blog.tags.split(',').some(tag => selectedTags.includes(tag.trim().toLowerCase()))
        )
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // most recent first
        .slice(0, 3); // take maximum 3 blogs


    // Determining if it is a vowel of consonant
    function vowelOrConsonant(word: string) {
        if (!word) return "";
        return /^[aeiouAEIOU]/.test(word) ? "an" : "a";
    }

    function renderWithFormatting(text: string, keyPrefix = "") {
        // Handles hyperlink first:
        // 1. Multi-word links: [Multiple words]*(http://link)
        const multiWordLinkRegex = /\[([^\]]+)\]\*\((https?:\/\/[^\s)]+)\)/g;
        // 2. Single-word links: Word*(http://link)
        const singleWordLinkRegex = /(\S+)\*\((https?:\/\/[^\s)]+)\)/g;

        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        // First handle multi-word hyperlinks
        while ((match = multiWordLinkRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                const before = text.slice(lastIndex, match.index);
                parts.push(...applyFormatting(before, keyPrefix));
            }

            const displayText = match[1];
            const url = match[2];

            parts.push(
                <a
                    key={`${keyPrefix}-multi-link-${match.index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="to-link-class"
                >
                    {displayText}
                </a>
            );

            lastIndex = multiWordLinkRegex.lastIndex;
        }

        // Process remaining text for single-word hyperlinks
        const remainingText = text.slice(lastIndex);
        const innerParts: (string | JSX.Element)[] = [];
        let innerLastIndex = 0;
        let innerMatch;

        while ((innerMatch = singleWordLinkRegex.exec(remainingText)) !== null) {
            if (innerMatch.index > innerLastIndex) {
                const before = remainingText.slice(innerLastIndex, innerMatch.index);
                innerParts.push(...applyFormatting(before, keyPrefix));
            }

            const word = innerMatch[1];
            const url = innerMatch[2];

            innerParts.push(
                <a
                    key={`${keyPrefix}-link-${innerMatch.index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="to-link-class"
                >
                    {word}
                </a>
            );

            innerLastIndex = singleWordLinkRegex.lastIndex;
        }

        if (innerLastIndex < remainingText.length) {
            const remaining = remainingText.slice(innerLastIndex);
            innerParts.push(...applyFormatting(remaining, keyPrefix));
        }

        parts.push(...innerParts);
        return parts;
    }


    // Helper: apply bold, italics, underline, title
    function applyFormatting(text: string, keyPrefix = "") {
        const formatRegex = /(b|i|u|t)\[([^\]]+)\]/g;
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        while ((match = formatRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }

            const type = match[1];
            const content = match[2];
            let element: JSX.Element | null = null;

            switch (type) {
                case "b":
                    element = <strong key={`${keyPrefix}-b-${match.index}`}>{content}</strong>;
                    break;
                case "i":
                    element = <em key={`${keyPrefix}-i-${match.index}`}>{content}</em>;
                    break;
                case "u":
                    element = <u key={`${keyPrefix}-u-${match.index}`}>{content}</u>;
                    break;
                case "t":
                    element = <h2 className='title-from-admin' key={`${keyPrefix}-t-${match.index}`}>{content}</h2>;
                    break;
                default:
                    element = null;
            }

            if (element) parts.push(element);
            lastIndex = formatRegex.lastIndex;
        }

        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return parts;
    }

    // Adding the content for metadata
    if (!selectedBlog) return null; // or loading state
    const blogTitle = selectedBlog.title;
    const blogDescription = selectedBlog.summary || 'Stay Ahead of the Storm™';
    const blogImage = selectedBlog.mainImage || 'https://fireneuralnetwork.com/static/favicon.ico';
    const blogUrl = `${window.location.origin}/fountainn/blog/${selectedBlog.link_name}`;

    return (
        <div className="home-container blog-container-ext">
            <ToastContainer />

            <div className="blog-post-hero-section">
                <img className="contact-hero-image" alt="hero background" src={selectedBlog.mainImage} />
                <div className="dark-overlay darker-for-header"></div>

                <div className="overall-blog-post-wrapper">
                    <h2><strong>FountaiNN</strong></h2>
                    <h1>{selectedBlog.title}</h1>
                    <h2>Shared by {vowelOrConsonant(selectedBlog.specialty)} {selectedBlog.specialty} Specialist</h2>

                    <div className='blog-name-time'>

                        <div className='blog-clock'>
                            <span>by {selectedBlog.author}</span>
                            <span className='block-time-stick'>━</span>
                        </div>

                        <div className='blog-clock'>
                            <LuClock4 />
                            <span>{calculateReadTime(selectedBlog)} minute read</span>
                        </div>
                    </div>

                </div>

                {/* <div className='blog-post-credentials'>
                    <p>by {selectedBlog.author}</p>
                    <p>━</p>
                    <LuClock4 />
                    <p>{calculateReadTime(selectedBlog)} minute read</p>
                </div> */}
            </div>

            {/* Blog Body Blocks */}
            <div className='full-blog-content'>
                {selectedBlog.content_blocks
                    .sort((a, b) => a.order - b.order)
                    .map((block) => {
                        if (block.block_type === 'paragraph') {
                            return (
                                <div className='blog-post-body' key={block.contentID}>
                                    {block.text?.split('\n').map((line, idx) => {
                                        const trimmed = line.trim();
                                        const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');

                                        if (isBullet) {
                                            return (
                                                <ul key={idx}>
                                                    <li>{renderWithFormatting(trimmed.slice(1).trim(), `line-${idx}`)}</li>
                                                </ul>
                                            );
                                        } else {
                                            return <p key={idx}>{renderWithFormatting(line, `line-${idx}`)}</p>

                                        }
                                    })}

                                </div>

                            );
                        }


                        if (block.block_type === 'quote') {
                            return (
                                <div className='blog-post-quote' key={block.contentID}>
                                    <BiSolidQuoteLeft className='quote-icon' />
                                    <h2>
                                        {block.text}
                                        <BiSolidQuoteRight className='quote-icon closing-quote' />
                                    </h2>
                                </div>
                            );
                        }


                        if (block.block_type === 'image' && block.image) {
                            return (
                                <div className='blog-post-image-and-title' key={block.contentID}>
                                    <h1>{block.image_title}</h1>
                                    <img
                                        alt={block.image_title || 'Blog visual'}
                                        src={block.image}
                                        className="blog-post-media"
                                    />
                                    <p style={{ color: '#6C757D' }}>{block.image_description}</p>
                                </div>
                            );
                        }

                        if (block.block_type === 'video' && block.video_url) {
                            const videoId = getVideoId(block.video_url);
                            if (!videoId) return null;

                            return (
                                <div className='blog-post-image-and-title' key={block.contentID}>
                                    {block.image_title && <h1>{block.image_title}</h1>}
                                    <div className="blog-post-video-wrapper blog-post-media">
                                        <iframe
                                            src={`https://player.vimeo.com/video/${videoId}`}
                                            title="FNN Vimeo video player"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                    {block.image_description && (
                                        <p style={{ color: '#6C757D' }}>{block.image_description}</p>
                                    )}
                                </div>
                            );
                        }




                        return null;
                    })}

                <p className='blog-post-tags'>
                    <strong>Tags:</strong>{' '}
                    {selectedBlog.tags
                        .split(',')
                        .map(tag => tag.trim())
                        .map((tag, index, arr) => (
                            <span key={index}>
                                <u>{tag}</u>
                                {index < arr.length - 1 ? ', ' : ''}
                            </span>
                        ))}
                </p>

                <div style={{ width: '100%' }}>
                    <div className='author-picture-and-description'>
                        {/* <img></img> */}
                        <p><strong>{selectedBlog.author}</strong> {selectedBlog.about_author}</p>

                        <div className='follow-and-social'>
                            <h2>Follow Us</h2>

                            <div className='header-social-media-in-menu'>
                                <a
                                    href="https://www.youtube.com/@FNN_INC/videos"
                                    className="social-in-menu"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faYoutube} size="1x" />
                                </a>

                                <a
                                    href="https://www.linkedin.com/company/fireneuralnetwork/"
                                    className="social-in-menu"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faLinkedin} size="1x" />
                                </a>

                                <a
                                    href="https://x.com/_FNNINC"
                                    className="social-in-menu"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faXTwitter} size="1x" />
                                </a>

                            </div>
                        </div>


                    </div>
                </div>

                <div style={{ width: '100%' }}>
                    <div className='blog-suggestion-wrapper'>
                        <h2>YOU MAY ALSO LIKE</h2>

                        <div className='blog-post-suggetion-card-stack'>
                            {recommendedBlogs.map(blog => (
                                <RecommendedCard blog={blog} key={blog.postID} />
                            ))}
                        </div>
                    </div>
                </div>

            </div>

            <BlogSubscribeForm />

        </div>
    );
};

export default BlogPost;
